import React from 'react'
import {
    DeleteOutlined,
    ExpandOutlined,
    ReloadOutlined,
    ZoomInOutlined,
    ZoomOutOutlined,
} from '@ant-design/icons'
import { Button, Empty, Image, Space, Tag, Tooltip, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import { HistoryLabelOption, HistoryPrediction } from '../../../types/history'
import {
    buildLabelOptionColorMap,
    getAnnotationClassColor,
    getLabelOptionColor,
    getLabelOptionColorKey,
    getLabelOptionIdentityKey,
    getStableModelTypeColor,
    normalizeAnnotationTypeKey,
} from '../../../utils/annotationColors'
import { getPredictionDisplayCategory } from '../../../utils/mosquitoDisplay'
import { getImageUrl } from '../../../utils/urlHelper'
import { ImagePreviewHoverMask } from '../../common/ImagePreviewHoverMask'
import {
    buildReadonlyPreviewRender,
    READONLY_PREVIEW_IMAGE_STYLE,
} from '../../common/MediaPreview/readonlyPreviewRender'

const { Text } = Typography

const HANDLE_RADIUS_PX = 6
const HANDLE_HIT_RADIUS_PX = 12
const HANDLE_STROKE_WIDTH_PX = 2
const MIN_BOX_SIZE = 4
const MIN_ZOOM = 1
const MAX_ZOOM = 5
const ZOOM_STEP = 0.25
const DEFAULT_VIEWPORT_SCALE = 1
const BOX_STROKE_WIDTH_PX = 2.5
const SELECTED_BOX_STROKE_WIDTH_PX = 4
const LABEL_BORDER_STROKE_WIDTH_PX = 1.5
const LABEL_HEIGHT_PX = 24
const LABEL_GAP_PX = 2
const LABEL_PADDING_X_PX = 8
const LABEL_TEXT_BASELINE_PX = 16
const LABEL_FONT_SIZE_PX = 13
const LABEL_MIN_WIDTH_PX = 120
const LABEL_ASCII_CHAR_WIDTH_PX = 7
const LABEL_WIDE_CHAR_WIDTH_PX = 13
const MODEL_ANNOTATION_COLOR = '#00ff00'
const MODEL_ANNOTATION_LABEL_FILL = '#ffffff'
const MODEL_BOX_STROKE_WIDTH_PX = 3.5
const MODEL_LABEL_BORDER_STROKE_WIDTH_PX = 2
const MODEL_LABEL_HEIGHT_PX = 28
const MODEL_LABEL_PADDING_X_PX = 10
const MODEL_LABEL_TEXT_BASELINE_PX = 19
const MODEL_LABEL_FONT_SIZE_PX = 15
const MODEL_LABEL_FONT_WEIGHT = 800
const EMPTY_REFERENCE_PREDICTIONS: HistoryPrediction[] = []
const PREVIEW_SURFACE_BG = '#f6f8fb'
const PREVIEW_SURFACE_BORDER = '1px solid #f0f0f0'
const PREVIEW_SURFACE_RADIUS = 8
const PREVIEW_SECTION_PADDING_TOP_PX = 16
const PREVIEW_SECTION_PADDING_X_PX = 24
const PREVIEW_SECTION_PADDING_BOTTOM_PX = 12

interface BoxPoint {
    x: number
    y: number
}

interface BoxBounds {
    x1: number
    y1: number
    x2: number
    y2: number
}

interface EditablePrediction extends HistoryPrediction {
    localId: string
    cls_id: number
    eng_class: string
    confidence: number
    bbox: BoxBounds
}

type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se'
const getResizeHandleCursor = (
    handle: ResizeHandle,
): React.CSSProperties['cursor'] =>
    handle === 'nw' || handle === 'se' ? 'nwse-resize' : 'nesw-resize'

type PredictionOptionMatch =
    | {
          option: HistoryLabelOption
          source:
              | 'className'
              | 'displayCategory'
              | 'manualType'
              | 'rawClass'
              | 'rawType'
      }
    | {
          option: HistoryLabelOption
          source: 'clsId'
      }

type DragState =
    | {
          type: 'drawing'
          boxId: string
          startPoint: BoxPoint
      }
    | {
          type: 'moving'
          boxId: string
          startPoint: BoxPoint
          initialBounds: BoxBounds
      }
    | {
          type: 'resizing'
          boxId: string
          startPoint: BoxPoint
          initialBounds: BoxBounds
          handle: ResizeHandle
      }

interface PanState {
    startClientX: number
    startClientY: number
    startScrollLeft: number
    startScrollTop: number
}

interface ImageMetrics {
    naturalWidth: number
    naturalHeight: number
    displayWidth: number
    displayHeight: number
}

export type AnnotationActionPlacement = 'editor-toolbar' | 'modal-footer'
export type AnnotationZoomControlPlacement =
    | 'toolbar'
    | 'image-overlay'
    | 'hidden'
export type ReadonlyAnnotationStyle = 'editor' | 'model'

export interface AnnotationFooterActions {
    deleteDisabled: boolean
    resetDisabled: boolean
    onDeleteSelected: () => void
    onReset: () => void
}

const isWideLabelCharacter = (character: string): boolean => {
    const codePoint = character.codePointAt(0) || 0
    return (
        (codePoint >= 0x2e80 && codePoint <= 0x9fff) ||
        (codePoint >= 0xac00 && codePoint <= 0xd7af) ||
        (codePoint >= 0xf900 && codePoint <= 0xfaff) ||
        (codePoint >= 0xff00 && codePoint <= 0xffef)
    )
}

export const estimateAnnotationLabelWidth = (labelText: string): number => {
    const textWidth = Array.from(labelText).reduce(
        (width, character) =>
            width +
            (isWideLabelCharacter(character)
                ? LABEL_WIDE_CHAR_WIDTH_PX
                : LABEL_ASCII_CHAR_WIDTH_PX),
        0,
    )

    return Math.max(textWidth + LABEL_PADDING_X_PX * 2, LABEL_MIN_WIDTH_PX)
}

const estimateModelAnnotationLabelWidth = (labelText: string): number => {
    const textWidth = Array.from(labelText).reduce(
        (width, character) =>
            width +
            (isWideLabelCharacter(character)
                ? LABEL_WIDE_CHAR_WIDTH_PX
                : LABEL_ASCII_CHAR_WIDTH_PX),
        0,
    )

    return textWidth + MODEL_LABEL_PADDING_X_PX * 2
}

export const getAnnotationViewportScale = (
    metrics: ImageMetrics,
    zoom: number,
): number => {
    const horizontalScale =
        (metrics.displayWidth * zoom) / Math.max(metrics.naturalWidth, 1)
    const verticalScale =
        (metrics.displayHeight * zoom) / Math.max(metrics.naturalHeight, 1)
    const scale = Math.min(horizontalScale, verticalScale)

    return Number.isFinite(scale) && scale > 0 ? scale : DEFAULT_VIEWPORT_SCALE
}

interface DetectionAnnotationEditorProps {
    imageSrc?: string | null
    fallbackImageSrc?: string | null
    filename: string
    labelOptions: HistoryLabelOption[]
    seedPredictions: HistoryPrediction[]
    referencePredictions?: HistoryPrediction[]
    seedKey: string
    editable: boolean
    dirty: boolean
    actionPlacement?: AnnotationActionPlacement
    zoomControlPlacement?: AnnotationZoomControlPlacement
    readonlyAnnotationStyle?: ReadonlyAnnotationStyle
    showAnnotationLabels?: boolean
    containReadonlyPreview?: boolean
    readonlyPreviewOnImageClick?: boolean
    previewMaskClosable?: boolean
    defaultPredictionConfidence?: number
    onChange: (predictions: HistoryPrediction[]) => void
    onFooterActionsChange?: (actions: AnnotationFooterActions | null) => void
    onReset: () => void
}

const getPredictionBBox = (prediction: HistoryPrediction): BoxBounds | null => {
    if (prediction.bbox) {
        return {
            x1: Number(prediction.bbox.x1),
            y1: Number(prediction.bbox.y1),
            x2: Number(prediction.bbox.x2),
            y2: Number(prediction.bbox.y2),
        }
    }

    if (prediction.box && prediction.box.length === 4) {
        return {
            x1: Number(prediction.box[0]),
            y1: Number(prediction.box[1]),
            x2: Number(prediction.box[2]),
            y2: Number(prediction.box[3]),
        }
    }

    return null
}

const normalizeBounds = (bounds: BoxBounds): BoxBounds => {
    const left = Math.min(bounds.x1, bounds.x2)
    const right = Math.max(bounds.x1, bounds.x2)
    const top = Math.min(bounds.y1, bounds.y2)
    const bottom = Math.max(bounds.y1, bounds.y2)

    return {
        x1: left,
        y1: top,
        x2: right,
        y2: bottom,
    }
}

const clampBoundsToImage = (
    bounds: BoxBounds,
    metrics: ImageMetrics,
): BoxBounds => {
    const normalized = normalizeBounds(bounds)
    return {
        x1: Math.min(Math.max(normalized.x1, 0), metrics.naturalWidth),
        y1: Math.min(Math.max(normalized.y1, 0), metrics.naturalHeight),
        x2: Math.min(Math.max(normalized.x2, 0), metrics.naturalWidth),
        y2: Math.min(Math.max(normalized.y2, 0), metrics.naturalHeight),
    }
}

const clampZoom = (zoom: number): number =>
    Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number(zoom.toFixed(2))))

const buildPredictionFromOption = (
    option: HistoryLabelOption,
    bounds: BoxBounds,
    localId: string,
    confidence = 1,
): EditablePrediction => ({
    localId,
    model_key: option.model_key,
    raw_class: option.raw_class || option.eng_class,
    raw_class_norm:
        option.raw_class_norm ||
        normalizeClassKey(option.raw_class || option.eng_class),
    manual_type_key: option.manual_type_key,
    cls_id: option.cls_id,
    eng_class: option.eng_class,
    class: option.class,
    confidence,
    bbox: normalizeBounds(bounds),
})

const buildPredictionFromExisting = (
    prediction: HistoryPrediction,
    bounds: BoxBounds,
    localId: string,
): EditablePrediction => ({
    ...prediction,
    localId,
    cls_id: prediction.cls_id ?? -1,
    eng_class: prediction.eng_class || prediction.class || 'unknown',
    class: prediction.class || prediction.eng_class || 'unknown',
    confidence:
        typeof prediction.confidence === 'number' ? prediction.confidence : 1,
    bbox: normalizeBounds(bounds),
})

const toSerializablePredictions = (
    predictions: EditablePrediction[],
): HistoryPrediction[] =>
    predictions.map((prediction) => ({
        class: prediction.class,
        model_key: prediction.model_key,
        raw_class: prediction.raw_class,
        raw_class_norm: prediction.raw_class_norm,
        manual_type_key: prediction.manual_type_key,
        eng_class: prediction.eng_class,
        cls_id: prediction.cls_id,
        confidence: prediction.confidence,
        bbox: prediction.bbox,
    }))

const normalizeSeedPredictions = (
    predictions: HistoryPrediction[],
    labelOptions: HistoryLabelOption[],
): EditablePrediction[] => {
    return predictions
        .map((prediction, index) => {
            const bbox = getPredictionBBox(prediction)
            if (!bbox) {
                return null
            }

            const predictionLocalKey = normalizeClassKey(
                prediction.raw_class_norm ||
                    prediction.raw_class ||
                    prediction.eng_class ||
                    prediction.class,
            )
            const matchedOption = getPredictionOptionMatch(
                prediction,
                labelOptions,
            )

            const normalizedBounds = normalizeBounds(bbox)
            if (
                normalizedBounds.x2 - normalizedBounds.x1 <= 0 ||
                normalizedBounds.y2 - normalizedBounds.y1 <= 0
            ) {
                return null
            }

            if (!matchedOption) {
                return buildPredictionFromExisting(
                    prediction,
                    normalizedBounds,
                    `prediction-${index}-${predictionLocalKey || 'unknown'}`,
                )
            }

            if (
                matchedOption.source === 'clsId' ||
                matchedOption.source === 'rawType' ||
                matchedOption.option.manual_type_key
            ) {
                return buildPredictionFromOption(
                    matchedOption.option,
                    normalizedBounds,
                    `prediction-${index}-${matchedOption.option.cls_id}`,
                    typeof prediction.confidence === 'number'
                        ? prediction.confidence
                        : 1,
                )
            }

            return buildPredictionFromExisting(
                prediction,
                normalizedBounds,
                `prediction-${index}-${predictionLocalKey || matchedOption.option.cls_id}`,
            )
        })
        .filter(
            (prediction): prediction is EditablePrediction =>
                prediction !== null,
        )
}

const normalizeReferencePredictions = (
    predictions: HistoryPrediction[],
    editablePredictions: EditablePrediction[],
): EditablePrediction[] => {
    const editableBoxKeys = new Set(
        editablePredictions.map((prediction) => buildBoxKey(prediction.bbox)),
    )

    return predictions
        .map((prediction, index) => {
            const bbox = getPredictionBBox(prediction)
            if (!bbox) {
                return null
            }

            const normalizedBounds = normalizeBounds(bbox)
            if (
                normalizedBounds.x2 - normalizedBounds.x1 <= 0 ||
                normalizedBounds.y2 - normalizedBounds.y1 <= 0
            ) {
                return null
            }

            if (editableBoxKeys.has(buildBoxKey(normalizedBounds))) {
                return null
            }

            return {
                localId: `reference-${index}`,
                cls_id: prediction.cls_id ?? -1,
                eng_class:
                    prediction.eng_class || prediction.class || 'unknown',
                class: prediction.class || prediction.eng_class || 'unknown',
                confidence:
                    typeof prediction.confidence === 'number'
                        ? prediction.confidence
                        : 1,
                bbox: normalizedBounds,
            }
        })
        .filter(
            (prediction): prediction is EditablePrediction =>
                prediction !== null,
        )
}

const buildBoxKey = (bounds: BoxBounds): string => {
    const normalizedBounds = normalizeBounds(bounds)
    return [
        normalizedBounds.x1,
        normalizedBounds.y1,
        normalizedBounds.x2,
        normalizedBounds.y2,
    ]
        .map((value) => Number(value).toFixed(2))
        .join(':')
}

const getHandlePosition = (
    bounds: BoxBounds,
    handle: ResizeHandle,
): BoxPoint => {
    switch (handle) {
        case 'nw':
            return { x: bounds.x1, y: bounds.y1 }
        case 'ne':
            return { x: bounds.x2, y: bounds.y1 }
        case 'sw':
            return { x: bounds.x1, y: bounds.y2 }
        case 'se':
            return { x: bounds.x2, y: bounds.y2 }
    }
}

const getLabelForPrediction = (
    prediction: EditablePrediction,
    labelOptions: HistoryLabelOption[],
) => {
    const matchedOption = findMatchingLabelOption(prediction, labelOptions)

    if (matchedOption) {
        return matchedOption.display_name
    }

    return prediction.class || prediction.eng_class
}

const getModelLabelForPrediction = (prediction: EditablePrediction): string => {
    const className =
        prediction.display_category_name ||
        prediction.class ||
        prediction.display_category_key ||
        prediction.eng_class ||
        'unknown'
    const confidence =
        typeof prediction.confidence === 'number' &&
        Number.isFinite(prediction.confidence)
            ? prediction.confidence
            : 0

    return `${className} ${confidence.toFixed(2)}`
}

const normalizeClassKey = normalizeAnnotationTypeKey

const getPredictionIdentityKey = (prediction: HistoryPrediction): string => {
    const modelKey = normalizeClassKey(prediction.model_key)
    const rawClass = normalizeClassKey(
        prediction.raw_class_norm || prediction.raw_class,
    )
    return modelKey && rawClass ? `${modelKey}:${rawClass}` : ''
}

const getPredictionRawClassKey = (prediction: HistoryPrediction): string =>
    normalizeClassKey(prediction.raw_class_norm || prediction.raw_class)

const getOptionRawClassKey = (option: HistoryLabelOption): string =>
    normalizeClassKey(option.raw_class_norm || option.raw_class)

const getPredictionOptionMatch = (
    prediction: HistoryPrediction,
    labelOptions: HistoryLabelOption[],
): PredictionOptionMatch | undefined => {
    const predictionManualTypeKey = normalizeClassKey(
        prediction.manual_type_key,
    )
    const predictionDisplayCategory = normalizeClassKey(
        prediction.display_category_key,
    )
    const predictionIdentityKey = getPredictionIdentityKey(prediction)
    const predictionRawClassKey = getPredictionRawClassKey(prediction)
    const predictionEngClass = normalizeClassKey(prediction.eng_class)
    const predictionClass = normalizeClassKey(prediction.class)

    const matchedByRawType = labelOptions.find(
        (option) =>
            predictionIdentityKey &&
            getLabelOptionIdentityKey(option) === predictionIdentityKey,
    )
    if (matchedByRawType) {
        return { option: matchedByRawType, source: 'rawType' }
    }

    const matchedByManualType = labelOptions.find(
        (option) =>
            predictionManualTypeKey &&
            normalizeClassKey(option.manual_type_key) ===
                predictionManualTypeKey,
    )
    if (matchedByManualType) {
        return { option: matchedByManualType, source: 'manualType' }
    }

    const matchedByRawClass = labelOptions.find(
        (option) =>
            predictionRawClassKey &&
            getOptionRawClassKey(option) === predictionRawClassKey,
    )
    if (matchedByRawClass) {
        return { option: matchedByRawClass, source: 'rawClass' }
    }

    const matchedByDisplayCategory = labelOptions.find(
        (option) =>
            predictionDisplayCategory &&
            (normalizeClassKey(option.display_category_key) ===
                predictionDisplayCategory ||
                normalizeClassKey(option.eng_class) ===
                    predictionDisplayCategory ||
                normalizeClassKey(option.class) === predictionDisplayCategory),
    )
    if (matchedByDisplayCategory) {
        return { option: matchedByDisplayCategory, source: 'displayCategory' }
    }

    const matchedByClassName = labelOptions.find(
        (option) =>
            (predictionEngClass &&
                normalizeClassKey(option.eng_class) === predictionEngClass) ||
            (predictionClass &&
                normalizeClassKey(option.class) === predictionClass),
    )
    if (matchedByClassName) {
        return { option: matchedByClassName, source: 'className' }
    }

    if (!predictionEngClass && !predictionClass) {
        const matchedByClsId = labelOptions.find(
            (option) => option.cls_id === prediction.cls_id,
        )
        if (matchedByClsId) {
            return { option: matchedByClsId, source: 'clsId' }
        }
    }

    return undefined
}

const findMatchingLabelOption = (
    prediction: HistoryPrediction,
    labelOptions: HistoryLabelOption[],
): HistoryLabelOption | undefined =>
    getPredictionOptionMatch(prediction, labelOptions)?.option

const getPredictionColor = (
    prediction: EditablePrediction,
    labelOptions: HistoryLabelOption[],
    labelOptionColors: Map<string, string>,
): string => {
    const predictionDisplayCategory = normalizeClassKey(
        prediction.display_category_key,
    )
    const predictionIdentityKey = getPredictionIdentityKey(prediction)
    const predictionEngClass = normalizeClassKey(prediction.eng_class)
    const predictionClass = normalizeClassKey(prediction.class)
    const matchedOption = findMatchingLabelOption(prediction, labelOptions)
    if (matchedOption) {
        return getLabelOptionColor(matchedOption, labelOptionColors)
    }

    if (predictionIdentityKey) {
        return getStableModelTypeColor(predictionIdentityKey)
    }

    const classKey =
        predictionDisplayCategory || predictionEngClass || predictionClass

    return getAnnotationClassColor(classKey)
}

const getLegendOptionDedupeKey = (
    option: HistoryLabelOption,
    groupByDisplayName: boolean,
): string => {
    if (groupByDisplayName) {
        const displayNameKey = normalizeClassKey(option.display_name)
        if (displayNameKey) {
            return `display:${displayNameKey}`
        }
    }

    const identityKey = getLabelOptionIdentityKey(option)
    return identityKey ? `identity:${identityKey}` : `cls:${option.cls_id}`
}

const getLegendOptionRuntimeKey = (
    option: HistoryLabelOption,
    groupByDisplayName: boolean,
): string =>
    groupByDisplayName
        ? getLegendOptionDedupeKey(option, true)
        : getLabelOptionColorKey(option)

const buildLegendOptionColorMap = (
    legendOptions: HistoryLabelOption[],
    groupByDisplayName: boolean,
): Map<string, string> => {
    if (!groupByDisplayName) {
        return buildLabelOptionColorMap(legendOptions)
    }

    const colors = new Map<string, string>()
    legendOptions.forEach((option) => {
        const optionKey = getLegendOptionRuntimeKey(option, true)
        if (!colors.has(optionKey)) {
            colors.set(optionKey, getLabelOptionColor(option))
        }
    })
    return colors
}

const getLegendOptionColor = (
    option: HistoryLabelOption,
    labelOptionColors: Map<string, string>,
    groupByDisplayName: boolean,
): string =>
    labelOptionColors.get(
        getLegendOptionRuntimeKey(option, groupByDisplayName),
    ) || getLabelOptionColor(option, labelOptionColors)

const getPredictionDisplayGroupedColor = (
    prediction: EditablePrediction,
    labelOptions: HistoryLabelOption[],
    labelOptionColors: Map<string, string>,
    groupByDisplayName: boolean,
): string => {
    if (groupByDisplayName) {
        const matchedOption = findMatchingLabelOption(prediction, labelOptions)
        if (matchedOption) {
            const groupedColor = labelOptionColors.get(
                getLegendOptionRuntimeKey(matchedOption, true),
            )
            if (groupedColor) {
                return groupedColor
            }
        }
    }

    return getPredictionColor(prediction, labelOptions, labelOptionColors)
}

const canEditPredictionGeometry = (prediction: EditablePrediction): boolean =>
    Boolean(getPredictionIdentityKey(prediction)) ||
    Boolean(normalizeClassKey(prediction.manual_type_key)) ||
    getPredictionDisplayCategory(prediction) !== null

const getImagePointFromEvent = (
    event: MouseEvent | React.MouseEvent<SVGElement, MouseEvent>,
    overlay: SVGSVGElement,
    metrics: ImageMetrics,
): BoxPoint => {
    const rect = overlay.getBoundingClientRect()
    const x = ((event.clientX - rect.left) / rect.width) * metrics.naturalWidth
    const y = ((event.clientY - rect.top) / rect.height) * metrics.naturalHeight

    return {
        x: Math.min(Math.max(x, 0), metrics.naturalWidth),
        y: Math.min(Math.max(y, 0), metrics.naturalHeight),
    }
}

const updateMovingBounds = (
    bounds: BoxBounds,
    deltaX: number,
    deltaY: number,
    metrics: ImageMetrics,
): BoxBounds => {
    const width = bounds.x2 - bounds.x1
    const height = bounds.y2 - bounds.y1

    const nextX1 = Math.min(
        Math.max(bounds.x1 + deltaX, 0),
        metrics.naturalWidth - width,
    )
    const nextY1 = Math.min(
        Math.max(bounds.y1 + deltaY, 0),
        metrics.naturalHeight - height,
    )

    return {
        x1: nextX1,
        y1: nextY1,
        x2: nextX1 + width,
        y2: nextY1 + height,
    }
}

const updateResizedBounds = (
    bounds: BoxBounds,
    point: BoxPoint,
    handle: ResizeHandle,
    metrics: ImageMetrics,
): BoxBounds => {
    const nextBounds = { ...bounds }

    if (handle === 'nw' || handle === 'sw') {
        nextBounds.x1 = point.x
    } else {
        nextBounds.x2 = point.x
    }

    if (handle === 'nw' || handle === 'ne') {
        nextBounds.y1 = point.y
    } else {
        nextBounds.y2 = point.y
    }

    return clampBoundsToImage(nextBounds, metrics)
}

const isTextInputLike = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) {
        return false
    }

    return Boolean(
        target.closest(
            'input, textarea, [contenteditable="true"], .ant-select',
        ),
    )
}

export const DetectionAnnotationEditor: React.FC<
    DetectionAnnotationEditorProps
> = ({
    imageSrc,
    fallbackImageSrc,
    filename,
    labelOptions,
    seedPredictions,
    referencePredictions = EMPTY_REFERENCE_PREDICTIONS,
    seedKey,
    editable,
    dirty,
    actionPlacement = 'editor-toolbar',
    zoomControlPlacement = 'toolbar',
    readonlyAnnotationStyle = 'editor',
    showAnnotationLabels = true,
    readonlyPreviewOnImageClick = false,
    previewMaskClosable = true,
    defaultPredictionConfidence = 1,
    onChange,
    onFooterActionsChange,
    onReset,
}) => {
    const { t } = useTranslation('history')
    const imageRef = React.useRef<HTMLImageElement | null>(null)
    const overlayRef = React.useRef<SVGSVGElement | null>(null)
    const scrollContainerRef = React.useRef<HTMLDivElement | null>(null)
    const panMovedRef = React.useRef(false)
    const initializedSeedKeyRef = React.useRef<string | null>(null)
    const predictionsRef = React.useRef<EditablePrediction[]>([])
    const [metrics, setMetrics] = React.useState<ImageMetrics | null>(null)
    const [predictions, setPredictions] = React.useState<EditablePrediction[]>(
        [],
    )
    const [modelReferencePredictions, setModelReferencePredictions] =
        React.useState<EditablePrediction[]>([])
    const [selectedId, setSelectedId] = React.useState<string | null>(null)
    const [drawMode, setDrawMode] = React.useState(false)
    const [dragState, setDragState] = React.useState<DragState | null>(null)
    const [panState, setPanState] = React.useState<PanState | null>(null)
    const [zoom, setZoom] = React.useState(1)
    const [newBoxOptionKey, setNewBoxOptionKey] = React.useState<
        string | undefined
    >()
    const [imageLoadFailed, setImageLoadFailed] = React.useState(false)
    const [previewVisible, setPreviewVisible] = React.useState(false)
    const [previewHovered, setPreviewHovered] = React.useState(false)

    const replacePredictions = React.useCallback(
        (nextPredictions: EditablePrediction[]) => {
            predictionsRef.current = nextPredictions
            setPredictions(nextPredictions)
        },
        [],
    )

    const resolvedImageSrc = imageSrc || fallbackImageSrc || null
    const useModelReadonlyStyle =
        !editable && readonlyAnnotationStyle === 'model'
    const groupLegendByDisplayName = useModelReadonlyStyle
    const visibleLabelOptions = React.useMemo(
        () =>
            labelOptions.filter(
                (option) =>
                    getLabelOptionIdentityKey(option) ||
                    normalizeClassKey(option.manual_type_key) ||
                    getPredictionDisplayCategory({
                        class: option.class,
                        display_category_key:
                            option.display_category_key || undefined,
                        eng_class: option.eng_class,
                    }),
            ),
        [labelOptions],
    )
    const legendOptions = visibleLabelOptions.filter(
        (option, index, options) =>
            options.findIndex(
                (item) =>
                    getLegendOptionDedupeKey(item, groupLegendByDisplayName) ===
                    getLegendOptionDedupeKey(option, groupLegendByDisplayName),
            ) === index,
    )
    const labelOptionColors = React.useMemo(
        () =>
            buildLegendOptionColorMap(legendOptions, groupLegendByDisplayName),
        [groupLegendByDisplayName, legendOptions],
    )
    const legendPredictionCounts = React.useMemo(() => {
        const counts = new Map<string, number>()

        predictions.forEach((prediction) => {
            const matchedOption = findMatchingLabelOption(
                prediction,
                visibleLabelOptions,
            )
            if (!matchedOption) {
                return
            }

            const optionKey = getLegendOptionRuntimeKey(
                matchedOption,
                groupLegendByDisplayName,
            )
            counts.set(optionKey, (counts.get(optionKey) || 0) + 1)
        })

        return counts
    }, [groupLegendByDisplayName, predictions, visibleLabelOptions])

    const syncImageMetrics = React.useCallback(() => {
        const image = imageRef.current
        if (!image) {
            return
        }

        const nextMetrics = {
            naturalWidth: image.naturalWidth,
            naturalHeight: image.naturalHeight,
            displayWidth: image.clientWidth,
            displayHeight: image.clientHeight,
        }

        if (
            nextMetrics.naturalWidth > 0 &&
            nextMetrics.naturalHeight > 0 &&
            nextMetrics.displayWidth > 0 &&
            nextMetrics.displayHeight > 0
        ) {
            setMetrics(nextMetrics)
        }
    }, [])

    React.useEffect(() => {
        if (initializedSeedKeyRef.current === seedKey) {
            return
        }
        initializedSeedKeyRef.current = seedKey

        const normalizedPredictions = normalizeSeedPredictions(
            seedPredictions,
            visibleLabelOptions,
        )
        replacePredictions(normalizedPredictions)
        setModelReferencePredictions(
            normalizeReferencePredictions(
                referencePredictions,
                normalizedPredictions,
            ),
        )
        setSelectedId(normalizedPredictions[0]?.localId || null)
        setDrawMode(false)
        setDragState(null)
        setZoom(1)
    }, [
        referencePredictions,
        replacePredictions,
        seedKey,
        seedPredictions,
        visibleLabelOptions,
    ])

    React.useEffect(() => {
        if (visibleLabelOptions.length === 0) {
            setNewBoxOptionKey(undefined)
            return
        }

        setNewBoxOptionKey((current) => {
            if (
                current !== undefined &&
                visibleLabelOptions.some(
                    (option) => getLabelOptionColorKey(option) === current,
                )
            ) {
                return current
            }

            return getLabelOptionColorKey(visibleLabelOptions[0])
        })
    }, [visibleLabelOptions])

    React.useEffect(() => {
        setImageLoadFailed(false)
        setMetrics(null)
        setPreviewVisible(false)
        setPreviewHovered(false)
    }, [resolvedImageSrc])

    React.useEffect(() => {
        if (!imageRef.current) {
            return
        }

        syncImageMetrics()
        const observer = new ResizeObserver(() => {
            syncImageMetrics()
        })
        observer.observe(imageRef.current)

        window.addEventListener('resize', syncImageMetrics)
        return () => {
            observer.disconnect()
            window.removeEventListener('resize', syncImageMetrics)
        }
    }, [resolvedImageSrc, syncImageMetrics])

    const handleDeleteSelected = React.useCallback(() => {
        if (!editable || !selectedId) {
            return
        }

        const nextPredictions = predictionsRef.current.filter(
            (prediction) => prediction.localId !== selectedId,
        )
        replacePredictions(nextPredictions)
        setSelectedId(nextPredictions[0]?.localId || null)
        onChange(toSerializablePredictions(nextPredictions))
    }, [editable, onChange, replacePredictions, selectedId])

    const handleLegendOptionClick = React.useCallback((optionKey: string) => {
        setNewBoxOptionKey(optionKey)
        setSelectedId(null)
        setDrawMode(true)
    }, [])

    const handleZoomIn = React.useCallback(() => {
        setZoom((currentZoom) => clampZoom(currentZoom + ZOOM_STEP))
    }, [])

    const handleZoomOut = React.useCallback(() => {
        setZoom((currentZoom) => clampZoom(currentZoom - ZOOM_STEP))
    }, [])

    const handleZoomReset = React.useCallback(() => {
        setZoom(1)
    }, [])

    React.useEffect(() => {
        if (
            !editable ||
            actionPlacement !== 'modal-footer' ||
            !onFooterActionsChange
        ) {
            onFooterActionsChange?.(null)
            return
        }

        onFooterActionsChange({
            deleteDisabled: !selectedId,
            resetDisabled: !dirty,
            onDeleteSelected: handleDeleteSelected,
            onReset,
        })

        return () => {
            onFooterActionsChange(null)
        }
    }, [
        actionPlacement,
        dirty,
        editable,
        handleDeleteSelected,
        onFooterActionsChange,
        onReset,
        selectedId,
    ])

    React.useEffect(() => {
        if (!panState || !scrollContainerRef.current) {
            return
        }

        const handleMouseMove = (event: MouseEvent) => {
            const container = scrollContainerRef.current
            if (!container) {
                return
            }

            const deltaX = event.clientX - panState.startClientX
            const deltaY = event.clientY - panState.startClientY
            if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
                panMovedRef.current = true
            }

            container.scrollLeft = panState.startScrollLeft - deltaX
            container.scrollTop = panState.startScrollTop - deltaY
        }

        const handleMouseUp = () => {
            if (!panMovedRef.current) {
                setSelectedId(null)
            }
            setPanState(null)
            panMovedRef.current = false
        }

        window.addEventListener('mousemove', handleMouseMove)
        window.addEventListener('mouseup', handleMouseUp)
        return () => {
            window.removeEventListener('mousemove', handleMouseMove)
            window.removeEventListener('mouseup', handleMouseUp)
        }
    }, [panState])

    React.useEffect(() => {
        if (!dragState || !metrics || !overlayRef.current) {
            return
        }

        const handleMouseMove = (event: MouseEvent) => {
            const overlay = overlayRef.current
            if (!overlay) {
                return
            }

            const point = getImagePointFromEvent(event, overlay, metrics)
            const nextPredictions = predictionsRef.current.map((prediction) => {
                if (prediction.localId !== dragState.boxId) {
                    return prediction
                }

                if (dragState.type === 'drawing') {
                    return {
                        ...prediction,
                        bbox: clampBoundsToImage(
                            {
                                x1: dragState.startPoint.x,
                                y1: dragState.startPoint.y,
                                x2: point.x,
                                y2: point.y,
                            },
                            metrics,
                        ),
                    }
                }

                if (dragState.type === 'moving') {
                    return {
                        ...prediction,
                        bbox: updateMovingBounds(
                            dragState.initialBounds,
                            point.x - dragState.startPoint.x,
                            point.y - dragState.startPoint.y,
                            metrics,
                        ),
                    }
                }

                return {
                    ...prediction,
                    bbox: updateResizedBounds(
                        dragState.initialBounds,
                        point,
                        dragState.handle,
                        metrics,
                    ),
                }
            })
            replacePredictions(nextPredictions)

            if (dragState.type !== 'drawing') {
                onChange(toSerializablePredictions(nextPredictions))
            }
        }

        const handleMouseUp = () => {
            const currentPredictions = predictionsRef.current
            if (dragState.type !== 'drawing') {
                onChange(toSerializablePredictions(currentPredictions))
            } else {
                const targetPrediction = currentPredictions.find(
                    (prediction) => prediction.localId === dragState.boxId,
                )
                if (targetPrediction) {
                    const width =
                        targetPrediction.bbox.x2 - targetPrediction.bbox.x1
                    const height =
                        targetPrediction.bbox.y2 - targetPrediction.bbox.y1
                    if (width < MIN_BOX_SIZE || height < MIN_BOX_SIZE) {
                        const filteredPredictions = currentPredictions.filter(
                            (prediction) =>
                                prediction.localId !== dragState.boxId,
                        )
                        replacePredictions(filteredPredictions)
                        setSelectedId(filteredPredictions[0]?.localId || null)
                        onChange(toSerializablePredictions(filteredPredictions))
                    } else {
                        setSelectedId(dragState.boxId)
                        onChange(toSerializablePredictions(currentPredictions))
                    }
                }
            }

            if (dragState.type !== 'drawing') {
                setDrawMode(false)
            }
            setDragState(null)
        }

        window.addEventListener('mousemove', handleMouseMove)
        window.addEventListener('mouseup', handleMouseUp)
        return () => {
            window.removeEventListener('mousemove', handleMouseMove)
            window.removeEventListener('mouseup', handleMouseUp)
        }
    }, [dragState, metrics, onChange, replacePredictions])

    React.useEffect(() => {
        if (!editable) {
            return
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (isTextInputLike(event.target)) {
                return
            }

            if (
                event.key.toLowerCase() === 'r' &&
                !event.ctrlKey &&
                !event.metaKey &&
                !event.altKey &&
                visibleLabelOptions.length > 0 &&
                newBoxOptionKey !== undefined
            ) {
                event.preventDefault()
                setDrawMode((current) => !current)
                return
            }

            if (
                event.key === 'Backspace' &&
                !event.ctrlKey &&
                !event.metaKey &&
                !event.altKey &&
                selectedId
            ) {
                event.preventDefault()
                handleDeleteSelected()
                return
            }

            if (event.key === 'Escape' && drawMode) {
                event.preventDefault()
                setDrawMode(false)
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [
        drawMode,
        editable,
        handleDeleteSelected,
        newBoxOptionKey,
        selectedId,
        visibleLabelOptions.length,
    ])

    if (!resolvedImageSrc || imageLoadFailed) {
        return <Empty description={t('detail.annotationEditorUnavailable')} />
    }

    const handleCanvasMouseDown = (
        event: React.MouseEvent<SVGSVGElement, MouseEvent>,
    ) => {
        if (!editable || !metrics || !overlayRef.current) {
            return
        }

        if (event.target !== event.currentTarget) {
            return
        }

        const point = getImagePointFromEvent(event, overlayRef.current, metrics)
        if (!drawMode) {
            setSelectedId(null)
            return
        }

        const initialOption =
            visibleLabelOptions.find(
                (option) => getLabelOptionColorKey(option) === newBoxOptionKey,
            ) || visibleLabelOptions[0]
        if (!initialOption) {
            return
        }

        const localId = `manual-${Date.now()}`
        const nextPrediction = buildPredictionFromOption(
            initialOption,
            {
                x1: point.x,
                y1: point.y,
                x2: point.x,
                y2: point.y,
            },
            localId,
            defaultPredictionConfidence,
        )

        replacePredictions([...predictionsRef.current, nextPrediction])
        setSelectedId(localId)
        setDragState({
            type: 'drawing',
            boxId: localId,
            startPoint: point,
        })
    }

    const handleViewportMouseDownCapture = (
        event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    ) => {
        if (
            event.button !== 0 ||
            drawMode ||
            zoom <= 1 ||
            dragState ||
            !scrollContainerRef.current
        ) {
            return
        }

        const target = event.target
        if (target !== overlayRef.current && target !== imageRef.current) {
            return
        }

        panMovedRef.current = false
        setPanState({
            startClientX: event.clientX,
            startClientY: event.clientY,
            startScrollLeft: scrollContainerRef.current.scrollLeft,
            startScrollTop: scrollContainerRef.current.scrollTop,
        })
        event.preventDefault()
        event.stopPropagation()
    }

    const handlePredictionMouseDown = (
        event: React.MouseEvent<SVGRectElement, MouseEvent>,
        prediction: EditablePrediction,
    ) => {
        event.stopPropagation()
        if (!editable || !metrics) {
            setSelectedId(prediction.localId)
            return
        }

        if (!canEditPredictionGeometry(prediction)) {
            setSelectedId(prediction.localId)
            setDrawMode(false)
            return
        }

        const overlay = overlayRef.current
        if (!overlay) {
            return
        }

        const point = getImagePointFromEvent(event, overlay, metrics)
        setSelectedId(prediction.localId)
        setDrawMode(false)
        setDragState({
            type: 'moving',
            boxId: prediction.localId,
            startPoint: point,
            initialBounds: prediction.bbox,
        })
    }

    const handleResizeMouseDown = (
        event: React.MouseEvent<SVGCircleElement, MouseEvent>,
        prediction: EditablePrediction,
        handle: ResizeHandle,
    ) => {
        event.stopPropagation()
        if (!editable || !metrics) {
            return
        }

        if (!canEditPredictionGeometry(prediction)) {
            setSelectedId(prediction.localId)
            setDrawMode(false)
            return
        }

        const overlay = overlayRef.current
        if (!overlay) {
            return
        }

        const point = getImagePointFromEvent(event, overlay, metrics)
        setSelectedId(prediction.localId)
        setDrawMode(false)
        setDragState({
            type: 'resizing',
            boxId: prediction.localId,
            startPoint: point,
            initialBounds: prediction.bbox,
            handle,
        })
    }

    const zoomPercent = Math.round(zoom * 100)
    const viewportScale = metrics
        ? getAnnotationViewportScale(metrics, zoom)
        : DEFAULT_VIEWPORT_SCALE
    const toImageUnits = (screenPixels: number) => screenPixels / viewportScale
    const boxStrokeWidth = toImageUnits(BOX_STROKE_WIDTH_PX)
    const selectedBoxStrokeWidth = toImageUnits(SELECTED_BOX_STROKE_WIDTH_PX)
    const labelBorderStrokeWidth = toImageUnits(LABEL_BORDER_STROKE_WIDTH_PX)
    const labelHeight = toImageUnits(LABEL_HEIGHT_PX)
    const labelGap = toImageUnits(LABEL_GAP_PX)
    const labelPaddingX = toImageUnits(LABEL_PADDING_X_PX)
    const labelTextBaseline = toImageUnits(LABEL_TEXT_BASELINE_PX)
    const labelFontSize = toImageUnits(LABEL_FONT_SIZE_PX)
    const modelBoxStrokeWidth = toImageUnits(MODEL_BOX_STROKE_WIDTH_PX)
    const modelLabelBorderStrokeWidth = toImageUnits(
        MODEL_LABEL_BORDER_STROKE_WIDTH_PX,
    )
    const modelLabelHeight = toImageUnits(MODEL_LABEL_HEIGHT_PX)
    const modelLabelPaddingX = toImageUnits(MODEL_LABEL_PADDING_X_PX)
    const modelLabelTextBaseline = toImageUnits(MODEL_LABEL_TEXT_BASELINE_PX)
    const modelLabelFontSize = toImageUnits(MODEL_LABEL_FONT_SIZE_PX)
    const handleRadius = toImageUnits(HANDLE_RADIUS_PX)
    const handleHitRadius = toImageUnits(HANDLE_HIT_RADIUS_PX)
    const handleStrokeWidth = toImageUnits(HANDLE_STROKE_WIDTH_PX)
    const stageWidth = metrics ? metrics.displayWidth * zoom : undefined
    const stageHeight = metrics ? metrics.displayHeight * zoom : undefined
    const viewerMinHeight = editable ? 420 : 320
    const viewerMaxHeight = editable ? 'calc(100vh - 320px)' : 560
    const imageMaxHeight = editable ? 'calc(100vh - 360px)' : 520
    const showEditActionsInToolbar =
        editable && actionPlacement === 'editor-toolbar'
    const showZoomControlsInToolbar = zoomControlPlacement === 'toolbar'
    const showToolbar =
        showEditActionsInToolbar ||
        (!editable && !readonlyPreviewOnImageClick) ||
        (editable && showZoomControlsInToolbar)
    const canPreviewReadonlyImage = useModelReadonlyStyle && Boolean(metrics)
    const renderReadonlyPreviewImage = buildReadonlyPreviewRender(
        (_info, motionStyle) => {
            if (!metrics) {
                return null
            }

            return (
                <svg
                    data-testid="annotation-preview-overlay"
                    viewBox={`0 0 ${metrics.naturalWidth} ${metrics.naturalHeight}`}
                    width={metrics.naturalWidth}
                    height={metrics.naturalHeight}
                    style={{
                        ...READONLY_PREVIEW_IMAGE_STYLE,
                        display: 'block',
                        pointerEvents: 'none',
                        ...motionStyle,
                    }}
                >
                    {predictions.map((prediction) => {
                        const color = getPredictionDisplayGroupedColor(
                            prediction,
                            visibleLabelOptions,
                            labelOptionColors,
                            groupLegendByDisplayName,
                        )
                        const labelText = getLabelForPrediction(
                            prediction,
                            visibleLabelOptions,
                        )
                        const previewLabelWidth = toImageUnits(
                            estimateAnnotationLabelWidth(labelText),
                        )
                        const previewLabelX = Math.min(
                            prediction.bbox.x1,
                            Math.max(
                                metrics.naturalWidth - previewLabelWidth,
                                0,
                            ),
                        )
                        const previewLabelY =
                            prediction.bbox.y1 - labelHeight - labelGap >= 0
                                ? prediction.bbox.y1 - labelHeight - labelGap
                                : prediction.bbox.y1 + labelGap
                        return (
                            <g key={prediction.localId}>
                                <rect
                                    data-testid="annotation-preview-box"
                                    data-annotation-color={color}
                                    x={prediction.bbox.x1}
                                    y={prediction.bbox.y1}
                                    width={
                                        prediction.bbox.x2 - prediction.bbox.x1
                                    }
                                    height={
                                        prediction.bbox.y2 - prediction.bbox.y1
                                    }
                                    fill="transparent"
                                    stroke={color}
                                    strokeWidth={MODEL_BOX_STROKE_WIDTH_PX}
                                    vectorEffect="non-scaling-stroke"
                                />
                                {showAnnotationLabels && (
                                    <>
                                        <rect
                                            data-testid="annotation-preview-label-background"
                                            x={previewLabelX}
                                            y={previewLabelY}
                                            width={previewLabelWidth}
                                            height={labelHeight}
                                            fill="rgba(255, 255, 255, 0.92)"
                                            stroke={color}
                                            strokeWidth={labelBorderStrokeWidth}
                                        />
                                        <text
                                            x={previewLabelX + labelPaddingX}
                                            y={
                                                previewLabelY +
                                                labelTextBaseline
                                            }
                                            fill={color}
                                            fontSize={labelFontSize}
                                            fontWeight="bold"
                                            dominantBaseline="alphabetic"
                                        >
                                            {labelText}
                                        </text>
                                    </>
                                )}
                            </g>
                        )
                    })}
                </svg>
            )
        },
    )
    const renderModelReferencePrediction = (prediction: EditablePrediction) => {
        const width = prediction.bbox.x2 - prediction.bbox.x1
        const height = prediction.bbox.y2 - prediction.bbox.y1
        const displayLabelText = getModelLabelForPrediction(prediction)
        const labelWidth = toImageUnits(
            estimateModelAnnotationLabelWidth(displayLabelText),
        )
        const labelX = Math.min(
            prediction.bbox.x1,
            Math.max((metrics?.naturalWidth ?? 0) - labelWidth, 0),
        )
        const labelY =
            prediction.bbox.y1 - modelLabelHeight - labelGap >= 0
                ? prediction.bbox.y1 - modelLabelHeight - labelGap
                : prediction.bbox.y1 + labelGap
        const labelTextY = labelY + modelLabelTextBaseline

        return (
            <g key={prediction.localId} data-testid="annotation-reference-box">
                <rect
                    data-testid="annotation-box"
                    x={prediction.bbox.x1}
                    y={prediction.bbox.y1}
                    width={width}
                    height={height}
                    fill="transparent"
                    stroke={MODEL_ANNOTATION_COLOR}
                    strokeWidth={modelBoxStrokeWidth}
                    pointerEvents="none"
                />
                {showAnnotationLabels && (
                    <>
                        <rect
                            data-testid="annotation-label-background"
                            x={labelX}
                            y={labelY}
                            width={labelWidth}
                            height={modelLabelHeight}
                            fill={MODEL_ANNOTATION_LABEL_FILL}
                            stroke={MODEL_ANNOTATION_COLOR}
                            strokeWidth={modelLabelBorderStrokeWidth}
                            pointerEvents="none"
                        />
                        <text
                            x={labelX + modelLabelPaddingX}
                            y={labelTextY}
                            fill={MODEL_ANNOTATION_COLOR}
                            fontSize={modelLabelFontSize}
                            fontWeight={MODEL_LABEL_FONT_WEIGHT}
                            dominantBaseline="alphabetic"
                            style={{
                                pointerEvents: 'none',
                                userSelect: 'none',
                            }}
                        >
                            {displayLabelText}
                        </text>
                    </>
                )}
            </g>
        )
    }
    const zoomControls = (
        <Space wrap size={8}>
            <Button
                icon={<ZoomOutOutlined />}
                disabled={zoom <= MIN_ZOOM}
                onClick={handleZoomOut}
            >
                {t('actions.zoomOut')}
            </Button>
            <Button onClick={handleZoomReset}>
                {t('actions.resetZoom', { zoom: zoomPercent })}
            </Button>
            <Button
                icon={<ZoomInOutlined />}
                disabled={zoom >= MAX_ZOOM}
                onClick={handleZoomIn}
            >
                {t('actions.zoomIn')}
            </Button>
        </Space>
    )
    const legendSection =
        legendOptions.length > 0 ? (
            <div
                data-testid="annotation-preview-legend"
                style={{
                    width: '100%',
                    paddingTop: PREVIEW_SECTION_PADDING_TOP_PX,
                    paddingRight: PREVIEW_SECTION_PADDING_X_PX,
                    paddingBottom: PREVIEW_SECTION_PADDING_BOTTOM_PX,
                    paddingLeft: PREVIEW_SECTION_PADDING_X_PX,
                    background: PREVIEW_SURFACE_BG,
                }}
            >
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Text strong>{t('detail.annotationColorLegendTitle')}</Text>
                    <Space wrap size={[8, 8]}>
                        {legendOptions.map((option) => {
                            const color = getLegendOptionColor(
                                option,
                                labelOptionColors,
                                groupLegendByDisplayName,
                            )
                            const optionKey = getLegendOptionRuntimeKey(
                                option,
                                groupLegendByDisplayName,
                            )
                            const isActive = optionKey === newBoxOptionKey
                            const content = (
                                <>
                                    <span
                                        data-testid="annotation-legend-color"
                                        data-annotation-color={color}
                                        style={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: '50%',
                                            background: color,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <span>
                                        {option.display_name}(
                                        {legendPredictionCounts.get(
                                            optionKey,
                                        ) || 0}
                                        )
                                    </span>
                                </>
                            )

                            return editable ? (
                                <button
                                    type="button"
                                    key={
                                        getLabelOptionIdentityKey(option) ||
                                        option.cls_id
                                    }
                                    onClick={() =>
                                        handleLegendOptionClick(optionKey)
                                    }
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        padding: '2px 10px',
                                        border: '1px solid #000000',
                                        borderRadius: 999,
                                        background: isActive
                                            ? `${color}1a`
                                            : '#ffffff',
                                        boxShadow: isActive
                                            ? '0 0 0 2px rgba(0, 0, 0, 0.12)'
                                            : 'none',
                                        cursor: 'pointer',
                                        font: 'inherit',
                                        color: 'inherit',
                                        outline: 'none',
                                    }}
                                >
                                    {content}
                                </button>
                            ) : (
                                <span
                                    key={
                                        getLabelOptionIdentityKey(option) ||
                                        option.cls_id
                                    }
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        padding: '2px 10px',
                                        border: '1px solid #000000',
                                        borderRadius: 999,
                                        background: '#ffffff',
                                    }}
                                >
                                    {content}
                                </span>
                            )
                        })}
                    </Space>
                </Space>
            </div>
        ) : null

    return (
        <>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {showToolbar && (
                    <Space
                        wrap
                        style={{
                            width: '100%',
                            justifyContent: 'space-between',
                        }}
                    >
                        {showEditActionsInToolbar ? (
                            <Space wrap>
                                <Button
                                    icon={<DeleteOutlined />}
                                    disabled={!selectedId}
                                    onClick={handleDeleteSelected}
                                >
                                    {t('actions.deleteSelectedBox')}
                                </Button>
                                <Button
                                    icon={<ReloadOutlined />}
                                    disabled={!dirty}
                                    onClick={onReset}
                                >
                                    {t('actions.resetAnnotations')}
                                </Button>
                            </Space>
                        ) : canPreviewReadonlyImage &&
                          !readonlyPreviewOnImageClick ? (
                            <Tooltip title={t('detail.imagePreview')}>
                                <Button
                                    type="text"
                                    icon={<ExpandOutlined />}
                                    aria-label={t('detail.imagePreview')}
                                    onClick={() => setPreviewVisible(true)}
                                />
                            </Tooltip>
                        ) : (
                            <span />
                        )}
                        {showZoomControlsInToolbar && (
                            <div data-testid="annotation-zoom-toolbar">
                                {zoomControls}
                            </div>
                        )}
                    </Space>
                )}

                <div
                    style={{
                        width: '100%',
                        border: PREVIEW_SURFACE_BORDER,
                        borderRadius: PREVIEW_SURFACE_RADIUS,
                        overflow: 'hidden',
                        background: PREVIEW_SURFACE_BG,
                    }}
                >
                    {legendSection}
                    <div
                        data-testid="annotation-preview-surface"
                        ref={scrollContainerRef}
                        onMouseDownCapture={handleViewportMouseDownCapture}
                        onWheel={(event) => {
                            if (!event.altKey) {
                                return
                            }

                            event.preventDefault()
                            setZoom((currentZoom) =>
                                clampZoom(
                                    currentZoom +
                                        (event.deltaY < 0
                                            ? ZOOM_STEP
                                            : -ZOOM_STEP),
                                ),
                            )
                        }}
                        style={{
                            position: 'relative',
                            width: '100%',
                            minHeight: viewerMinHeight,
                            maxHeight: viewerMaxHeight,
                            overflow: 'auto',
                            background: PREVIEW_SURFACE_BG,
                        }}
                    >
                        {zoomControlPlacement === 'image-overlay' && (
                            <div
                                data-testid="annotation-zoom-overlay"
                                style={{
                                    position: 'sticky',
                                    top: 8,
                                    right: 8,
                                    zIndex: 20,
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    padding: 8,
                                    pointerEvents: 'none',
                                }}
                            >
                                <div
                                    style={{
                                        padding: '6px 8px',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: 6,
                                        background: 'rgba(255, 255, 255, 0.96)',
                                        boxShadow:
                                            '0 2px 8px rgba(0, 0, 0, 0.12)',
                                        pointerEvents: 'auto',
                                    }}
                                >
                                    {zoomControls}
                                </div>
                            </div>
                        )}
                        <div
                            style={{
                                width: stageWidth || '100%',
                                height: stageHeight || '100%',
                                minHeight: viewerMinHeight,
                                margin: zoom === 1 ? '0 auto' : undefined,
                                position: 'relative',
                            }}
                        >
                            <div
                                data-testid="annotation-image-stage"
                                onClick={() => {
                                    if (
                                        readonlyPreviewOnImageClick &&
                                        canPreviewReadonlyImage &&
                                        zoom === 1
                                    ) {
                                        setPreviewVisible(true)
                                    }
                                }}
                                onMouseEnter={() => setPreviewHovered(true)}
                                onMouseLeave={() => setPreviewHovered(false)}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: metrics?.displayWidth,
                                    height: metrics?.displayHeight,
                                    lineHeight: 0,
                                    transform: `scale(${zoom})`,
                                    transformOrigin: 'top left',
                                    cursor:
                                        readonlyPreviewOnImageClick &&
                                        canPreviewReadonlyImage &&
                                        zoom === 1
                                            ? 'zoom-in'
                                            : undefined,
                                }}
                            >
                                <img
                                    ref={imageRef}
                                    src={getImageUrl(resolvedImageSrc)}
                                    alt={filename}
                                    draggable={false}
                                    onLoad={syncImageMetrics}
                                    onError={() => setImageLoadFailed(true)}
                                    style={{
                                        width: 'auto',
                                        height: 'auto',
                                        maxWidth: '100%',
                                        maxHeight: imageMaxHeight,
                                        objectFit: 'contain',
                                        userSelect: 'none',
                                        display: 'block',
                                    }}
                                />
                                {metrics && (
                                    <svg
                                        data-testid="annotation-overlay"
                                        ref={overlayRef}
                                        viewBox={`0 0 ${metrics.naturalWidth} ${metrics.naturalHeight}`}
                                        onMouseDown={handleCanvasMouseDown}
                                        style={{
                                            position: 'absolute',
                                            inset: 0,
                                            width: '100%',
                                            height: '100%',
                                            cursor: drawMode
                                                ? 'crosshair'
                                                : panState
                                                  ? 'grabbing'
                                                  : zoom > 1
                                                    ? 'grab'
                                                    : 'default',
                                        }}
                                    >
                                        {modelReferencePredictions.map(
                                            renderModelReferencePrediction,
                                        )}
                                        {predictions.map((prediction) => {
                                            const isSelected =
                                                prediction.localId ===
                                                selectedId
                                            const canEditGeometry =
                                                editable &&
                                                !useModelReadonlyStyle &&
                                                canEditPredictionGeometry(
                                                    prediction,
                                                )
                                            const width =
                                                prediction.bbox.x2 -
                                                prediction.bbox.x1
                                            const height =
                                                prediction.bbox.y2 -
                                                prediction.bbox.y1
                                            const labelText =
                                                getLabelForPrediction(
                                                    prediction,
                                                    visibleLabelOptions,
                                                )
                                            const displayLabelText = labelText
                                            const predictionColor =
                                                getPredictionDisplayGroupedColor(
                                                    prediction,
                                                    visibleLabelOptions,
                                                    labelOptionColors,
                                                    groupLegendByDisplayName,
                                                )
                                            const textColor = predictionColor
                                            const labelFill =
                                                'rgba(255, 255, 255, 0.92)'
                                            const labelStrokeWidth =
                                                labelBorderStrokeWidth
                                            const labelCornerRadius =
                                                toImageUnits(4)
                                            const displayLabelHeight =
                                                labelHeight
                                            const displayLabelPaddingX =
                                                labelPaddingX
                                            const displayLabelTextBaseline =
                                                labelTextBaseline
                                            const displayLabelFontSize =
                                                labelFontSize
                                            const displayLabelFontWeight =
                                                'bold'
                                            const labelWidth = toImageUnits(
                                                estimateAnnotationLabelWidth(
                                                    displayLabelText,
                                                ),
                                            )
                                            const labelX = Math.min(
                                                prediction.bbox.x1,
                                                Math.max(
                                                    metrics.naturalWidth -
                                                        labelWidth,
                                                    0,
                                                ),
                                            )
                                            const labelY =
                                                prediction.bbox.y1 -
                                                    displayLabelHeight -
                                                    labelGap >=
                                                0
                                                    ? prediction.bbox.y1 -
                                                      displayLabelHeight -
                                                      labelGap
                                                    : prediction.bbox.y1 +
                                                      labelGap
                                            const labelTextY =
                                                labelY +
                                                displayLabelTextBaseline

                                            return (
                                                <g key={prediction.localId}>
                                                    <rect
                                                        data-testid="annotation-box"
                                                        data-annotation-color={
                                                            predictionColor
                                                        }
                                                        x={prediction.bbox.x1}
                                                        y={prediction.bbox.y1}
                                                        width={width}
                                                        height={height}
                                                        fill="transparent"
                                                        stroke={predictionColor}
                                                        strokeWidth={
                                                            isSelected &&
                                                            !useModelReadonlyStyle
                                                                ? selectedBoxStrokeWidth
                                                                : useModelReadonlyStyle
                                                                  ? modelBoxStrokeWidth
                                                                  : boxStrokeWidth
                                                        }
                                                        onMouseDown={(event) =>
                                                            handlePredictionMouseDown(
                                                                event,
                                                                prediction,
                                                            )
                                                        }
                                                        style={{
                                                            cursor: canEditGeometry
                                                                ? 'move'
                                                                : undefined,
                                                        }}
                                                    />
                                                    {showAnnotationLabels && (
                                                        <>
                                                            <rect
                                                                data-testid="annotation-label-background"
                                                                x={labelX}
                                                                y={labelY}
                                                                width={
                                                                    labelWidth
                                                                }
                                                                height={
                                                                    displayLabelHeight
                                                                }
                                                                rx={
                                                                    labelCornerRadius
                                                                }
                                                                fill={labelFill}
                                                                stroke={
                                                                    predictionColor
                                                                }
                                                                strokeWidth={
                                                                    labelStrokeWidth
                                                                }
                                                            />
                                                            <text
                                                                x={
                                                                    labelX +
                                                                    displayLabelPaddingX
                                                                }
                                                                y={labelTextY}
                                                                fill={textColor}
                                                                fontSize={
                                                                    displayLabelFontSize
                                                                }
                                                                fontWeight={
                                                                    displayLabelFontWeight
                                                                }
                                                                dominantBaseline="alphabetic"
                                                                style={{
                                                                    pointerEvents:
                                                                        'none',
                                                                    userSelect:
                                                                        'none',
                                                                }}
                                                            >
                                                                {
                                                                    displayLabelText
                                                                }
                                                            </text>
                                                        </>
                                                    )}
                                                    {isSelected &&
                                                        canEditGeometry &&
                                                        (
                                                            [
                                                                'nw',
                                                                'ne',
                                                                'sw',
                                                                'se',
                                                            ] as ResizeHandle[]
                                                        ).map((handle) => {
                                                            const point =
                                                                getHandlePosition(
                                                                    prediction.bbox,
                                                                    handle,
                                                                )
                                                            const cursor =
                                                                getResizeHandleCursor(
                                                                    handle,
                                                                )
                                                            return (
                                                                <React.Fragment
                                                                    key={handle}
                                                                >
                                                                    <circle
                                                                        data-testid="annotation-resize-hit-area"
                                                                        cx={
                                                                            point.x
                                                                        }
                                                                        cy={
                                                                            point.y
                                                                        }
                                                                        r={
                                                                            handleHitRadius
                                                                        }
                                                                        fill="transparent"
                                                                        stroke="none"
                                                                        pointerEvents="all"
                                                                        style={{
                                                                            cursor,
                                                                        }}
                                                                        onMouseDown={(
                                                                            event,
                                                                        ) =>
                                                                            handleResizeMouseDown(
                                                                                event,
                                                                                prediction,
                                                                                handle,
                                                                            )
                                                                        }
                                                                    />
                                                                    <circle
                                                                        data-testid="annotation-resize-handle"
                                                                        cx={
                                                                            point.x
                                                                        }
                                                                        cy={
                                                                            point.y
                                                                        }
                                                                        r={
                                                                            handleRadius
                                                                        }
                                                                        fill="#ffffff"
                                                                        stroke={
                                                                            predictionColor
                                                                        }
                                                                        strokeWidth={
                                                                            handleStrokeWidth
                                                                        }
                                                                        pointerEvents="none"
                                                                        style={{
                                                                            cursor,
                                                                        }}
                                                                    />
                                                                </React.Fragment>
                                                            )
                                                        })}
                                                </g>
                                            )
                                        })}
                                    </svg>
                                )}
                                <ImagePreviewHoverMask
                                    label={t('detail.imagePreview')}
                                    visible={
                                        previewHovered &&
                                        readonlyPreviewOnImageClick &&
                                        canPreviewReadonlyImage &&
                                        zoom === 1
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {dirty && (
                    <Space wrap>
                        <Tag color="gold">{t('detail.annotationDirty')}</Tag>
                    </Space>
                )}
            </Space>
            {canPreviewReadonlyImage && (
                <Image
                    src={getImageUrl(resolvedImageSrc)}
                    alt={filename}
                    wrapperStyle={{ display: 'none' }}
                    preview={{
                        visible: previewVisible,
                        maskClosable: previewMaskClosable,
                        onVisibleChange: setPreviewVisible,
                        imageRender: renderReadonlyPreviewImage,
                    }}
                />
            )}
        </>
    )
}
